import React, { createContext, ReactNode, useCallback, useState } from 'react';
import { Platform, Pressable } from 'react-native';
import { X } from '@/lib/icons/X';
import { cn } from '@/lib/utils';
import { RandomUtils } from '@fytness/shared';
import { useContext } from '@/hooks/useContext';

export interface BaseModalProps {
  onClose: () => void;
}

export interface ModalProps extends BaseModalProps {
  [key: string]: unknown;
}

interface ModalState {
  id: string;
  component: React.ComponentType<ModalProps>;
  props: Omit<ModalProps, 'onClose'>;
  portalHost?: string;
}

interface ModalContextType {
  showModal: <T extends Omit<ModalProps, 'onClose'>>(
    component: React.ComponentType<ModalProps>,
    props?: T,
    options?: {
      portalHost?: string;
    },
  ) => string;
  hideModal: (id: string) => void;
  hideAllModals: () => void;
  modals: ModalState[];
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  return useContext(ModalContext);
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<ModalState[]>([]);

  const showModal = useCallback<ModalContextType['showModal']>(
    (component, props = {} as unknown, options = {}) => {
      const id = RandomUtils.uuid();
      const newModal: ModalState = {
        id,
        component,
        props: props as ModalProps,
        portalHost: options.portalHost,
      };

      setModals((prev) => [...prev, newModal]);
      return id;
    },
    [],
  );

  const hideModal = useCallback((id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  const hideAllModals = useCallback(() => {
    setModals([]);
  }, []);

  const contextValue: ModalContextType = {
    showModal,
    hideModal,
    hideAllModals,
    modals,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modals.map((modal) => {
        const ModalComponent = modal.component;
        return (
          <ModalComponent
            key={modal.id}
            {...modal.props}
            portalHost={modal.portalHost}
            onClose={() => hideModal(modal.id)}
          />
        );
      })}
    </ModalContext.Provider>
  );
};

// Modal Close Button Component
interface ModalCloseButtonProps {
  onClose?: () => void;
  className?: string;
  size?: number;
}

export const ModalCloseButton: React.FC<ModalCloseButtonProps> = ({
  onClose,
  className,
  size = Platform.OS === 'web' ? 16 : 18,
}) => {
  const modal = useModal();

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      // Close the most recent modal if no specific onClose is provided
      const lastModal = modal.modals[modal.modals.length - 1];
      if (lastModal) {
        modal.hideModal(lastModal.id);
      }
    }
  }, [onClose, modal]);

  return (
    <Pressable
      onPress={handleClose}
      className={cn(
        'absolute right-4 top-4 p-0.5 web:group rounded-sm opacity-70 web:ring-offset-background web:transition-opacity web:hover:opacity-100 web:focus:outline-none web:focus:ring-2 web:focus:ring-ring web:focus:ring-offset-2 web:disabled:pointer-events-none',
        className,
      )}
    >
      <X size={size} className="text-muted-foreground" />
    </Pressable>
  );
};

// Convenience component for Modal namespace
export const Modal = {
  CloseButton: ModalCloseButton,
};
